import { Divider, List, ListItem, Stack, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { Button, Confirmation } from 'core/components';
import { VendorQuoteHelper } from 'quotes/helpers';
import { Color, Level, Size, TooltipPosition, Variant } from 'theme/enums';

import { BlockedQuoteList } from './BlockedQuoteList';
import { IBulkSendAsSpaSelection } from './types';
import {
  formatVendorQuoteNames,
  getBulkSendAsSpaButtonLabelKey,
  getBulkSendAsSpaStatusCounts,
} from './utils';

interface IProps extends IBulkSendAsSpaSelection {
  pastValidUntilQuoteIds: number[];
  onSend: () => void;
  onCancel: () => void;
}

export const BulkSendAsSpaWarning = ({
  blockers,
  omits,
  alreadySent,
  firstSends,
  resends,
  pastValidUntilQuoteIds,
  onSend,
  onCancel,
}: IProps) => {
  const { t } = useTranslation();
  const { user } = useAuthUser();
  const spaAudienceRights = VendorQuoteHelper.getSpaAudienceRights(user);

  const eligibleCount = firstSends.length + resends.length;

  const contentLines = [
    { key: 'labelBulkSendAsSpaFirstSends', count: firstSends.length },
    { key: 'labelBulkSendAsSpaResends', count: resends.length },
  ]
    .filter(({ count }) => count > 0)
    .map(({ key, count }) => ({ id: key, text: t(key, { count }) }));

  const spaStatusLines = getBulkSendAsSpaStatusCounts([...firstSends, ...resends]).map(
    ({ status, count }) => ({
      id: status,
      text: t('labelBulkSendAsSpaStatusCount', {
        count,
        status: t(VendorQuoteHelper.getSpaStatusLabelKey(status) ?? ''),
      }),
    })
  );

  const renderBulletList = (lines: { id: string; text: string }[]) => (
    <List marker="disc" sx={{ lineHeight: '21px' }}>
      {lines.map(({ id, text }) => (
        <ListItem key={id}>{text}</ListItem>
      ))}
    </List>
  );

  const message = (
    <Stack gap={2}>
      {eligibleCount > 0 && (
        <Stack gap={0}>
          <Typography level={Level.BODY_MD}>
            {t('messageBulkSendAsSpaLead', { count: eligibleCount })}
          </Typography>
          {renderBulletList(contentLines)}
        </Stack>
      )}

      {spaStatusLines.length > 0 && (
        <Stack gap={0}>
          <Typography level={Level.TITLE_SM}>{t('labelSpaStatus')}</Typography>
          {renderBulletList(spaStatusLines)}
        </Stack>
      )}

      {blockers.length > 0 && (
        <Stack gap={0}>
          <Typography color={Color.DANGER} level={Level.TITLE_MD}>
            {t('titleBulkSendAsSpaBlockers')} ({blockers.length})
          </Typography>
          <Typography level={Level.BODY_SM}>{t('messageBulkSendAsSpaBlockers')}</Typography>
          <BlockedQuoteList quotes={blockers} withReason spaAudienceRights={spaAudienceRights} />
        </Stack>
      )}

      {omits.length > 0 && (
        <>
          {blockers.length > 0 && <Divider />}

          <Stack gap={0}>
            <Typography color={Color.WARNING} level={Level.TITLE_MD}>
              {t('titleBulkSendAsSpaOmits')} ({omits.length})
            </Typography>
            <Typography level={Level.BODY_SM}>{t('messageBulkSendAsSpaOmits')}</Typography>
            <BlockedQuoteList quotes={omits} />
          </Stack>
        </>
      )}

      {resends.length > 0 && (
        <>
          {(blockers.length > 0 || omits.length > 0) && <Divider />}

          <Stack gap={0}>
            <Typography color={Color.WARNING} level={Level.TITLE_MD}>
              {t('titleBulkSendAsSpaResends')} ({resends.length})
            </Typography>
            <Typography level={Level.BODY_SM}>{t('messageBulkSendAsSpaResends')}</Typography>
            <BlockedQuoteList quotes={resends} />
          </Stack>
        </>
      )}

      {alreadySent.length > 0 && (
        <>
          {(blockers.length > 0 || omits.length > 0 || resends.length > 0) && <Divider />}

          <Stack gap={0}>
            <Typography color={Color.SUCCESS} level={Level.TITLE_MD}>
              {t('titleBulkSendAsSpaAlreadySent')} ({alreadySent.length})
            </Typography>
            <Typography level={Level.BODY_SM}>{t('messageBulkSendAsSpaAlreadySent')}</Typography>
            <BlockedQuoteList quotes={alreadySent} />
          </Stack>
        </>
      )}

      {pastValidUntilQuoteIds.length > 0 && (
        <>
          {(blockers.length > 0 ||
            omits.length > 0 ||
            resends.length > 0 ||
            alreadySent.length > 0) && <Divider />}

          <Typography color={Color.WARNING} level={Level.BODY_MD}>
            {t('messageBulkSendAsSpaPastValidUntil', {
              quotes: formatVendorQuoteNames(pastValidUntilQuoteIds, [...firstSends, ...resends]),
            })}
          </Typography>
        </>
      )}
    </Stack>
  );

  // Send action lives in extraButtons: only that slot forwards `disabled` (onConfirm /
  // confirmationActions Buttons in Confirmation do not), so it's the only way to keep the
  // primary action disabled while blockers remain.
  const extraButtons = [
    <Button
      key="bulkSendAsSpa"
      text={t(getBulkSendAsSpaButtonLabelKey({ firstSends, resends }), { count: eligibleCount })}
      variant={Variant.SOLID}
      color={Color.PRIMARY}
      disabled={blockers.length > 0 || eligibleCount === 0}
      tooltipProps={
        blockers.length > 0
          ? {
              placement: TooltipPosition.TOP,
              color: Color.NEUTRAL,
              size: Size.SM,
              title: t('tooltipBulkSendAsSpaBlockers'),
              children: null,
            }
          : undefined
      }
      onClick={onSend}
    />,
  ];

  return (
    <Confirmation
      isOpen={true}
      title={t('titleBulkSendAsSpa')}
      message={message}
      onCancel={onCancel}
      color={blockers.length > 0 ? Color.DANGER : Color.WARNING}
      extraButtons={extraButtons}
      dialogSx={{ maxWidth: 600 }}
    />
  );
};
